<script lang="ts" setup>
import { NModal, NButton, NSpin } from 'naive-ui';
import { DialogType } from '@/constants/dialog-type';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';

interface Props {
  visible: boolean;
  title: string;
  confirmText?: string;
  cancelText?: string;
  dialogType: DialogType;
  loading?: boolean;
  modalLoading?: boolean;
  width?: number | string;
}

const {t} = useI18n();

const props = withDefaults(defineProps<Props>(), {
  dialogType: DialogType.Confirm,
});
const emits = defineEmits(['cancel', 'confirm']);

const defaultTexts = {
  [DialogType.Delete]: { cancel: 'core.cancel', confirm: 'core.delete' },
  [DialogType.Confirm]: { cancel: 'core.cancel', confirm: 'core.save' }
}

const computedCancelText = computed(() =>
  props.cancelText ?? defaultTexts[props.dialogType].cancel
)
const computedConfirmText = computed(() =>
  props.confirmText ?? defaultTexts[props.dialogType].confirm
)
</script>

<template>
  <n-modal
    v-model:show="props.visible"
    preset="dialog"
    :show-icon="false"
    :bordered="false"
    :title="props.title"
    closable
    @close="emits('cancel')"
    :style="props.width ? { width: props.width + 'px' } : {}"
    class="confirmation-modal"
  >
    <n-spin :show="props.modalLoading">
      <slot name="content"></slot>
    </n-spin>

    <template #action>
      <n-button quaternary size="small" @click="emits('cancel')">
        {{ t(computedCancelText) }}
      </n-button>
      <n-button :type="props.dialogType" :loading="props.loading || props.modalLoading" size="small" @click="emits('confirm')">
        {{ t(computedConfirmText) }}
      </n-button>
    </template>
  </n-modal>
</template>

<style lang="scss">
.confirmation-modal {
    .n-dialog__action {
      margin: 0 calc(-1 * var(--dis-space-3_0x)) calc(-1 * var(--dis-space-3_0x));
      padding: var(--dis-space-2_0x) var(--dis-space-3_0x);
      border-top: 1px solid var(--dis-color-line-primary);
    }
}
</style>